import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, Clock, RefreshCw, Wifi, WifiOff, Activity, ChevronRight, ClipboardList, Plus, Share2, Settings, AlertCircle, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { getBeltConnectionState, useDeviceWebSocket, type BeltConnectionState } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions } from '../api/sessions';
import { getMyDevices } from '../api/devices';
import { SkeletonBlock } from '../components/SkeletonBlock';
const DASHBOARD_WAVEFORM_SAMPLES_PER_SECOND = 50;
const DASHBOARD_QUEUE_CAPACITY = DASHBOARD_WAVEFORM_SAMPLES_PER_SECOND * 10;
const DASHBOARD_DISPLAYED_SAMPLES = 400;
type BeltStatusPresentation = {
  title: string;
  message: string;
  badgeClassName: string;
  dotClassName: string;
  cardClassName: string;
  cardTextClassName: string;
  showPing: boolean;
};
function getBeltStatusPresentation(state: BeltConnectionState): BeltStatusPresentation {
  switch (state) {
    case 'live':
    case 'signal_weak':
      return {
        title: 'Live',
        message: 'Live · Belt connected',
        badgeClassName: 'bg-success/15 text-success',
        dotClassName: 'bg-success',
        cardClassName: 'bg-accent/15 text-accent',
        cardTextClassName: 'text-accent',
        showPing: true
      };
    case 'reconnecting':
      return {
        title: 'Reconnecting',
        message: 'Reconnecting to belt…',
        badgeClassName: 'bg-amber-500/15 text-amber-700',
        dotClassName: 'bg-amber-500',
        cardClassName: 'bg-amber-500/15 text-amber-600',
        cardTextClassName: 'text-amber-600',
        showPing: false
      };
    case 'offline':
      return {
        title: 'Belt offline',
        message: "Can't reach your belt — check it's powered on and nearby.",
        badgeClassName: 'bg-danger/10 text-danger',
        dotClassName: 'bg-danger',
        cardClassName: 'bg-danger/10 text-danger',
        cardTextClassName: 'text-danger',
        showPing: false
      };
    default:
      return {
        title: 'Waiting for belt',
        message: 'Turn on your belt to begin monitoring.',
        badgeClassName: 'bg-secondary text-text-secondary',
        dotClassName: 'bg-text-secondary/60',
        cardClassName: 'bg-border text-text-secondary',
        cardTextClassName: 'text-text-secondary',
        showPing: false
      };
  }
}
function appendToDashboardQueue(queue: number[], batch: number[]) {
  if (batch.length >= DASHBOARD_QUEUE_CAPACITY) {
    queue.length = 0;
    queue.push(...batch.slice(-DASHBOARD_QUEUE_CAPACITY));
    return;
  }
  const overflow = queue.length + batch.length - DASHBOARD_QUEUE_CAPACITY;
  if (overflow > 0) queue.splice(0, overflow);
  queue.push(...batch);
}
export function Dashboard() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const {
    isConnected,
    lastMessageAgeSeconds,
    bpm,
    piezoValue,
    waveformData,
    babyWaveBatch
  } = useDeviceWebSocket();
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(bpm, isConnected);
  void piezoValue;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [device, setDevice] = useState<any>(null);
  const [stats, setStats] = useState({
    avgBpm: 0,
    timeMonitored: 0
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isWaveformPaused, setIsWaveformPaused] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const playbackQueueRef = useRef<number[]>([]);
  const displayBufferRef = useRef<number[]>([]);
  const waveformPausedRef = useRef(false);
  const hasLiveWaveformPlaybackRef = useRef(false);
  const hasHydratedWaveformRef = useRef(false);
  const initialWaveformBatchRef = useRef<number[] | null>(null);
  // Fetch Data
  const fetchData = async () => {
    if (!token) return;
    setError(false);
    try {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const [sessionsRes, devicesRes] = await Promise.all([getSessions(token, {
        from: startOfToday.toISOString()
      }), getMyDevices(token)]);
      setSessions(sessionsRes || []);
      if (devicesRes && devicesRes.length > 0) {
        setDevice(devicesRes[0]);
      }
      // Compute stats
      if (sessionsRes && sessionsRes.length > 0) {
        let totalDuration = 0;
        let totalBpm = 0;
        let bpmCount = 0;
        sessionsRes.forEach((s: any) => {
          if (s.duration) totalDuration += s.duration;
          if (s.avgBpm) {
            totalBpm += s.avgBpm;
            bpmCount++;
          }
        });
        setStats({
          avgBpm: bpmCount > 0 ? Math.round(totalBpm / bpmCount) : 0,
          timeMonitored: totalDuration
        });
      }
    } catch (err) {
      // console.error('Failed to load dashboard data', err)
      setError(true);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [token]);
  // Pull to refresh
  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (window.scrollY === 0 && touchStartY.current > 0) {
      const touchEndY = e.changedTouches[0].clientY;
      if (touchEndY - touchStartY.current > 60) {
        setIsRefreshing(true);
        fetchData();
      }
    }
    touchStartY.current = 0;
  };
  // Restore the shared trace immediately; the latest batch is already visible,
  // so it must not be replayed into the local queue.
  useEffect(() => {
    if (hasHydratedWaveformRef.current || waveformData.length === 0) return;
    displayBufferRef.current = waveformData.slice(-DASHBOARD_DISPLAYED_SAMPLES);
    playbackQueueRef.current = [];
    initialWaveformBatchRef.current = babyWaveBatch;
    hasHydratedWaveformRef.current = true;
  }, [babyWaveBatch, waveformData]);
  useEffect(() => {
    if (babyWaveBatch.length === 0) return;
    if (initialWaveformBatchRef.current === babyWaveBatch) {
      initialWaveformBatchRef.current = null;
      return;
    }
    appendToDashboardQueue(playbackQueueRef.current, babyWaveBatch);
    hasLiveWaveformPlaybackRef.current = true;
    if (waveformPausedRef.current) {
      waveformPausedRef.current = false;
      setIsWaveformPaused(false);
    }
  }, [babyWaveBatch]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let previousFrameTime: number | null = null;
    let samplesDue = 0;
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      const dpr = window.devicePixelRatio || 1;
      if (!parent) return dpr;
      const width = Math.max(1, Math.floor(parent.clientWidth * dpr));
      const height = Math.max(1, Math.floor(parent.clientHeight * dpr));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return dpr;
    };
    const drawWaveform = (data: number[], width: number, height: number, strokeStyle: string, lineWidth: number) => {
      if (data.length === 0) return;
      const minValue = Math.min(...data);
      const maxValue = Math.max(...data);
      const observedRange = maxValue - minValue;
      const visualRange = Math.max(observedRange * 1.25, Number.EPSILON);
      const baseline = minValue - (visualRange - observedRange) / 2;
      const paddingY = height * 0.1;
      const usableHeight = height - paddingY * 2;
      const step = width / Math.max(DASHBOARD_DISPLAYED_SAMPLES - 1, 1);
      const startX = width - (data.length - 1) * step;
      const mapY = (value: number) => height - paddingY - (value - baseline) / visualRange * usableHeight;
      ctx.beginPath();
      ctx.moveTo(startX, mapY(data[0]));
      for (let index = 1; index < data.length; index += 1) {
        ctx.lineTo(startX + index * step, mapY(data[index]));
      }
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();
    };
    const draw = (frameTime: number) => {
      const dpr = resizeCanvas();
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      const elapsedSeconds = previousFrameTime === null ? 0 : (frameTime - previousFrameTime) / 1000;
      previousFrameTime = frameTime;
      samplesDue += elapsedSeconds * DASHBOARD_WAVEFORM_SAMPLES_PER_SECOND;
      while (samplesDue >= 1 && playbackQueueRef.current.length > 0) {
        samplesDue -= 1;
        const sample = playbackQueueRef.current.shift();
        if (sample === undefined) continue;
        displayBufferRef.current.push(sample);
        if (displayBufferRef.current.length > DASHBOARD_DISPLAYED_SAMPLES) {
          displayBufferRef.current.shift();
        }
      }
      if (playbackQueueRef.current.length === 0) samplesDue = 0;
      const isPaused = hasLiveWaveformPlaybackRef.current && playbackQueueRef.current.length === 0;
      if (waveformPausedRef.current !== isPaused) {
        waveformPausedRef.current = isPaused;
        setIsWaveformPaused(isPaused);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(dpr, dpr);
      if (!isConnected && displayBufferRef.current.length === 0) {
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.strokeStyle = '#8A8A8A';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#8A8A8A';
        ctx.font = '14px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for signal...', width / 2, height / 2 - 15);
      } else {
        const data = displayBufferRef.current.slice(-DASHBOARD_DISPLAYED_SAMPLES);
        drawWaveform(data, width, height, 'rgba(107, 33, 232, 0.3)', 6);
        drawWaveform(data, width, height, '#6B21E8', 2);
      }
      ctx.restore();
      animationRef.current = requestAnimationFrame(draw);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animationRef.current = requestAnimationFrame(draw);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected]);
  // Helpers
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const getFirstName = () => {
    if (!user?.fullName) return 'Mama';
    return user.fullName.split(' ')[0];
  };
  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0h 0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return '--';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    return 'Yesterday';
  };
  const getBpmStatus = (currentBpm: number | null) => {
    if (!isConnected || !currentBpm) return {
      label: 'No signal',
      classes: 'bg-border text-text-secondary'
    };
    if (currentBpm < 110) return {
      label: 'Below normal',
      classes: 'bg-danger/15 text-danger'
    };
    if (currentBpm > 160) return {
      label: 'Above normal',
      classes: 'bg-danger/15 text-danger'
    };
    return {
      label: 'Normal range',
      classes: 'bg-accent/20 text-accent'
    };
  };
  const bpmStatus = getBpmStatus(bpm);
  const beltConnectionState = getBeltConnectionState(lastMessageAgeSeconds, isWaveformPaused);
  const beltStatus = getBeltStatusPresentation(beltConnectionState);
  return <main className="w-full h-full flex flex-col" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-862" data-mp="3gtu8os09mh5n">
      {isRefreshing && <div className="flex justify-center py-2" data-id="element-863" data-mp="3lrcc62vik469">
          <RefreshCw size={20} className="text-primary animate-spin" data-id="element-864" data-mp="25e5mw3yqzo6f" />
        </div>}

      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-865" data-mp="2ae0bu1yyq3ou">
          <div className="flex items-center text-danger" data-id="element-866" data-mp="setxttbu8ghv">
            <AlertCircle size={18} className="mr-2" data-id="element-867" data-mp="nhajyuj1y5qg" />
            <span className="text-[14px] font-medium" data-id="element-868" data-mp="ngsi9idmezb5">
              Some data could not be loaded. Pull down to refresh.
            </span>
          </div>
          <button onClick={fetchData} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-869" data-mp="xeonxunsh21v">
            Retry
          </button>
        </div>}

      {/* Desktop Grid Layout */}
      <div className="flex flex-col xl:grid xl:grid-cols-[1fr_380px] xl:gap-8" data-id="element-870" data-mp="4s3zb1yp7420">
        {/* LEFT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8" data-id="element-871" data-mp="ij8r5hvtqs1s">
          {/* Header */}
          <div className="flex justify-between items-start" data-id="element-872" data-mp="dpo3ojruxggu">
            <div data-id="element-873" data-mp="3m6itoynk36di">
              <h1 className="text-[22px] font-semibold text-text-primary" data-id="element-874" data-mp="3m5zdpnhgus24">
                {getGreeting()}, {getFirstName()}
              </h1>
              <p className="text-[14px] text-text-secondary mt-1" data-id="element-875" data-mp="3i3mk1xfzuklp">
                {formatDate()}
              </p>
            </div>
            <div className="flex items-center space-x-4 xl:hidden" data-id="element-876" data-mp="3nu67szitfgd">
              <button onClick={() => navigate('/app/alerts')} className="relative p-2" aria-label="View alerts" data-id="element-877" data-mp="3m0uhza1w51i">
                <Bell size={22} className="text-text-primary" data-id="element-878" data-mp="dfhp35i0ho68" />
                {/* Alert badge would go here if we had global alert state */}
              </button>
              <button onClick={() => navigate('/app/profile')} className="w-[36px] h-[36px] bg-primary rounded-full flex items-center justify-center text-white text-[14px] font-semibold" aria-label="View profile" data-id="element-879" data-mp="me3dvie2tzfg">
                {getFirstName()[0]}
              </button>
            </div>
          </div>

          {/* Live Monitor Hero Card */}
          <div className="bg-white rounded-[24px] shadow-elevated p-5 w-full" data-id="element-880" data-mp="2y5gxth6vs19w">
            <div className="flex justify-between items-center mb-4" data-id="element-881" data-mp="2ql4vlahhrq2a">
              <div className="min-w-0 flex-1 pr-3" data-id="element-882" data-mp="2lu6dq6guujwx">
                <h2 className="text-[16px] font-semibold text-text-primary" data-id="element-883" data-mp="2ltehqujcik64">
                  Live Monitor
                </h2>
                <span className={`mt-2 inline-flex max-w-full items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12px] font-semibold ${beltStatus.badgeClassName}`} role="status" aria-live="polite" data-id="element-884" data-mp="2gsz6c8imjmqs">
                  <span className="relative flex h-1.5 w-1.5 flex-shrink-0" data-id="element-885" data-mp="34lz5jxk6tz32">
                    {beltStatus.showPing && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" data-id="element-886" data-mp="2znnvblpzdmkb" />}
                    <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${beltStatus.dotClassName}`} data-id="element-887" data-mp="1o6o6j02udpv1" />
                  </span>
                  <span className="truncate" data-id="element-888" data-mp="b85hz8oasbg6">{beltStatus.message}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-889" data-mp="f9zer5vdgvb2">
                <button onClick={toggleSound} className="w-[36px] h-[36px] rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary/5 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-890" data-mp="f7d5n1su27qq">
                  {soundEnabled ? <Volume2 size={18} data-id="element-891" data-mp="3shamvi5u6v0f" /> : <VolumeX size={18} data-id="element-892" data-mp="3eokzb9anti17" />}
                </button>
                <button onClick={() => navigate('/app/monitor')} className="border border-primary text-primary text-[13px] font-medium px-[14px] py-[6px] rounded-pill hover:bg-primary/5 transition-colors h-[36px]" data-id="element-893" data-mp="3eq5un14st504">
                  Full Screen
                </button>
              </div>
            </div>

            <div className="w-full h-[140px] xl:h-[180px] bg-background rounded-[16px] relative overflow-hidden mb-4" data-id="element-894" data-mp="2q36hxeoihkek">
              <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live fetal waveform played back at 50 samples per second" data-id="element-895" data-mp="2m3615ndxwx4c" />
              {beltConnectionState === 'signal_weak' && <div className="absolute left-3 bottom-3 inline-flex items-center gap-1.5 rounded-pill bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-amber-700 shadow-sm" role="status" aria-live="polite" data-id="element-896" data-mp="3919d6b7nkdkt">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" data-id="element-897" data-mp="3dt0tlsx3h2x8" />
                  Signal weak · trace paused
                </div>}
            </div>

            <div className="flex items-end justify-between" data-id="element-898" data-mp="3nrqbvmswkpe0">
              <div className="flex items-center space-x-6" data-id="element-899" data-mp="3ntb77en1kccx">
                <div className="flex flex-col" data-id="element-900" data-mp="28fxir40322tj">
                  <motion.span key={bpm} initial={{
                  scale: 1.1
                }} animate={{
                  scale: 1
                }} className="text-[48px] font-bold text-primary leading-none" aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-901" data-mp="28h8m52pm98k4">
                    {isConnected && bpm ? bpm : '--'}
                  </motion.span>
                  <span className="text-[12px] text-text-secondary mt-1" data-id="element-902" data-mp="1zmkbsy56p0og">
                    BPM
                  </span>
                </div>

                <div className="w-[1px] h-[40px] bg-border" data-id="element-903" data-mp="24fn9nigzuwag" />

                <div className="flex flex-col" data-id="element-904" data-mp="16x3scpzjku4d">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-905" data-mp="16wc2lvsmlqm0">
                    Min
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-906" data-mp="3p819b2tnes84">
                    --
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-907" data-mp="3l3tfwsc8wo3k">
                  <span className="text-[12px] text-text-secondary mb-1" data-id="element-908" data-mp="3g61qq62zjg32">
                    Max
                  </span>
                  <span className="text-[20px] font-semibold text-text-primary leading-none" data-id="element-909" data-mp="3b78cgbfvjl1u">
                    --
                  </span>
                </div>
              </div>

              <div className={`px-3 py-1 rounded-pill text-[13px] font-medium ${bpmStatus.classes}`} data-id="element-910" data-mp="31gvt2mjspxxa">
                {bpmStatus.label}
              </div>
            </div>
          </div>

          {/* Recent Sessions (Desktop Left Col, Mobile Stacked) */}
          <div className="w-full" data-id="element-911" data-mp="2nife68bs5142">
            <div className="flex justify-between items-center mb-4" data-id="element-912" data-mp="2nhe15yb7e4jp">
              <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-913" data-mp="2ik41zqy0ieio">
                Recent Sessions
              </h2>
              <button onClick={() => navigate('/app/history')} className="text-[14px] text-primary hover:underline" data-id="element-914" data-mp="2ha7sp56zl08o">
                See all
              </button>
            </div>

            {isLoading ? <div className="space-y-3" data-id="element-915" data-mp="23bhvzqo2x2ku">
                {[1, 2, 3].map(i => <SkeletonBlock key={i} height="72px" data-id="element-916" data-mp="1yblpnkrwsz24" />)}
              </div> : sessions.length > 0 ? <div className="space-y-3" data-id="element-917" data-mp="1tk3whiw4o2rp">
                {sessions.slice(0, 3).map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[16px] shadow-sm p-4 flex items-center cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-918" data-mp="1omu35s5mb6yl" data-mpi={i}>
                    <div className="w-[40px] h-[40px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-919" data-mp="1an2p5rf3pqkl">
                      <Activity size={18} className="text-white" data-id="element-920" data-mp="3r8h7il459fgw" />
                    </div>
                    <div className="flex-1" data-id="element-921" data-mp="3vab5glmyg5dd">
                      <h3 className="text-[14px] font-semibold text-text-primary" data-id="element-922" data-mp="3vbmaspw1tl6l">
                        {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                      </h3>
                      <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-923" data-mp="3ci9wurkdp0qr">
                        {formatDuration(session.duration)} · Avg{' '}
                        {session.avgBpm || '--'} BPM
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-text-secondary" data-id="element-924" data-mp="2otqqwiosx01u" />
                  </div>)}
              </div> : <div className="bg-secondary rounded-[16px] p-6 flex flex-col items-center justify-center text-center" data-id="element-925" data-mp="3s3bjcgsosi9c">
                <ClipboardList size={40} className="text-primary mb-3" data-id="element-926" data-mp="3n48p7re8vwg5" />
                <h3 className="text-[16px] font-semibold text-text-primary mb-1" data-id="element-927" data-mp="4ralrai8ulh8">
                  No sessions yet
                </h3>
                <p className="text-[14px] text-text-secondary max-w-[200px]" data-id="element-928" data-mp="3960nz7a6e8z6">
                  Start monitoring and your sessions will appear here.
                </p>
              </div>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col space-y-6 xl:space-y-8 mt-6 xl:mt-0" data-id="element-929" data-mp="3i89xpuxmdybi">
          {/* Today's Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-4" data-id="element-930" data-mp="3da03pnnmt3rh">
            {/* Stat 1 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-931" data-mp="xiklzqs7qsn1">
              <div className="w-[32px] h-[32px] bg-primary/10 rounded-full flex items-center justify-center mb-3" data-id="element-932" data-mp="shem9904y540">
                <Heart size={16} className="text-primary" data-id="element-933" data-mp="1111j0hwjw6bl" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-934" data-mp="r0hg7atdutr9" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-935" data-mp="vzklmwd9z1zr">
                  {stats.avgBpm || '--'}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-936" data-mp="1jpyc4ig7oss8">
                Avg BPM today
              </span>
            </div>

            {/* Stat 2 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-937" data-mp="1a0505gc5qbng">
              <div className="w-[32px] h-[32px] bg-accent/20 rounded-full flex items-center justify-center mb-3" data-id="element-938" data-mp="1exwt8dojg3t8">
                <Clock size={16} className="text-accent" data-id="element-939" data-mp="22nr8t308s6h6" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="60%" className="mb-1" data-id="element-940" data-mp="27fsn0ugz079u" /> : <span className="text-[24px] font-bold text-text-primary leading-none mb-1" data-id="element-941" data-mp="itzf0fda1uqb">
                  {formatDuration(stats.timeMonitored)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-942" data-mp="9rghmas9gabg">
                Monitored today
              </span>
            </div>

            {/* Stat 3 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-943" data-mp="4vhusrmkz0d5">
              <div className="w-[32px] h-[32px] bg-amber-500/15 rounded-full flex items-center justify-center mb-3" data-id="element-944" data-mp="12j04c4o2g6i7">
                <RefreshCw size={16} className="text-amber-500" data-id="element-945" data-mp="sr45usi2iedg" />
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="80%" className="mb-1" data-id="element-946" data-mp="nsaub5mccxfx" /> : <span className="text-[20px] font-bold text-text-primary leading-tight mb-1 truncate" data-id="element-947" data-mp="1l755d4dc9ddb">
                  {getRelativeTime(device?.lastSeenAt)}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-948" data-mp="1ns326f460ngg">Last sync</span>
            </div>

            {/* Stat 4 */}
            <div className="bg-white rounded-[16px] shadow-sm p-4 flex flex-col" data-id="element-949" data-mp="1sq4p5b4pp6x7">
              <div className={`w-[32px] h-[32px] rounded-full flex items-center justify-center mb-3 ${beltStatus.cardClassName}`} data-id="element-950" data-mp="1xphkyldk9obx">
                {beltConnectionState === 'reconnecting' ? <RefreshCw size={16} className="animate-spin" data-id="element-951" data-mp="26pnqnl30pk0j" /> : beltConnectionState === 'offline' ? <WifiOff size={16} data-id="element-952" data-mp="2bnz4s80cigok" /> : <Wifi size={16} data-id="element-953" data-mp="2gg8pn9mlsqjz" />}
              </div>
              {isLoading ? <SkeletonBlock height="28px" width="70%" className="mb-1" data-id="element-954" data-mp="2lejypi52qx15" /> : <span className={`text-[18px] font-bold leading-tight mb-1 ${beltStatus.cardTextClassName}`} data-id="element-955" data-mp="2qfhh9vtzxt36">
                  {beltStatus.title}
                </span>}
              <span className="text-[13px] text-text-secondary" data-id="element-956" data-mp="vcb81gg0dame">
                Belt status
              </span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="w-full" data-id="element-957" data-mp="1443kfwolettm">
            <h2 className="text-[16px] font-semibold text-text-primary mb-3 xl:hidden" data-id="element-958" data-mp="1454v401p5eat">
              Quick Actions
            </h2>
            <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:gap-3 hide-scrollbar space-x-3 md:space-x-0" data-id="element-959" data-mp="1e2174fqt0ik4">
              <button onClick={() => navigate('/app/setup')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-960" data-mp="192516ayjqh1x">
                <Plus size={16} className="text-primary mr-2" data-id="element-961" data-mp="3ou2npsup0epw" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-962" data-mp="3ouce3hjngnw4">
                  Add Device
                </span>
              </button>
              <button onClick={() => navigate('/app/history')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-963" data-mp="3ao0nyu69hj2e">
                <Share2 size={16} className="text-primary mr-2" data-id="element-964" data-mp="30yh4pnemzn70" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-965" data-mp="35wse5x10rvop">
                  Share Report
                </span>
              </button>
              <button onClick={() => navigate('/app/alerts')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-966" data-mp="2r2w7e2kijb3l">
                <Bell size={16} className="text-primary mr-2" data-id="element-967" data-mp="2mzzn87fbr6xj" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-968" data-mp="g8rq9bhui3eu">
                  View Alerts
                </span>
              </button>
              <button onClick={() => navigate('/app/profile')} className="flex-shrink-0 flex items-center bg-white border border-primary/50 rounded-pill px-5 py-2.5 hover:bg-primary/5 transition-colors" data-id="element-969" data-mp="l7321rrpanzp">
                <Settings size={16} className="text-primary mr-2" data-id="element-970" data-mp="1k1yz6bk56kq1" />
                <span className="text-[14px] font-medium text-text-primary" data-id="element-971" data-mp="1k04ij9fwlerr">
                  Settings
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-972" data-mp="1az6b0h69ap4f">{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </main>;
}